// src/hooks/useHolidays.js - Holiday management hook
import { useCallback, useEffect, useState } from 'react';
import { FirebaseService } from '../services/FirebaseService';
import { useAuth } from './useAuth';

export const useHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Load holidays from Firebase
  const loadHolidays = useCallback(async () => {
    if (!user?.businessId) return;
    
    setLoading(true);
    try {
      const firebaseHolidays = await FirebaseService.getBusinessHolidays(user.businessId);
      
      // Convert Firebase data to array format
      const holidayArray = Object.entries(firebaseHolidays || {}).map(([id, data]) => ({
        id,
        ...data
      }));
      
      // Sort by start date
      holidayArray.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      
      setHolidays(holidayArray);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load holidays:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Add new holiday
  const addHoliday = async (holidayData) => {
    try {
      const result = await FirebaseService.addBusinessHoliday(user.businessId, holidayData);
      
      if (result.success) {
        // Add to local state
        setHolidays(prev => [...prev, result.holiday].sort((a, b) => 
          new Date(a.startDate) - new Date(b.startDate)
        ));
        return { success: true };
      }
      
      throw new Error('Failed to add holiday');
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Update existing holiday
  const updateHoliday = async (holidayId, updates) => {
    try {
      const result = await FirebaseService.updateBusinessHoliday(user.businessId, holidayId, updates);
      
      if (result.success) {
        // Update local state
        setHolidays(prev => prev.map(holiday => 
          holiday.id === holidayId 
            ? { ...holiday, ...updates, updatedAt: new Date().toISOString() }
            : holiday
        ).sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
        
        return { success: true };
      }
      
      throw new Error('Failed to update holiday');
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Delete holiday
  const deleteHoliday = async (holidayId) => {
    try {
      const result = await FirebaseService.deleteBusinessHoliday(user.businessId, holidayId);
      
      if (result.success) {
        // Remove from local state
        setHolidays(prev => prev.filter(holiday => holiday.id !== holidayId));
        return { success: true };
      }
      
      throw new Error('Failed to delete holiday');
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Get holidays by type
  const getHolidaysByType = (type) => {
    return holidays.filter(holiday => holiday.type === type);
  };

  // Get upcoming holidays
  const getUpcomingHolidays = (days = 30) => {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + days);
    
    return holidays.filter(holiday => {
      const startDate = new Date(holiday.startDate);
      return startDate >= now && startDate <= futureDate;
    });
  };

  // Check if a date is a holiday
  const isHolidayDate = (date) => {
    const checkDate = new Date(date);
    return holidays.some(holiday => {
      const startDate = new Date(holiday.startDate);
      const endDate = new Date(holiday.endDate || holiday.startDate);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Generate calendar integration
  const generateCalendarIntegration = async () => {
    try {
      const result = await FirebaseService.generateCalendarIntegration(user.businessId);
      
      if (result.success) {
        return { success: true, calendarData: result.calendarData };
      }
      
      throw new Error('Failed to generate calendar integration');
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Get calendar integration data
  const getCalendarIntegration = async () => {
    try {
      const calendarData = await FirebaseService.getCalendarIntegration(user.businessId);
      return { success: true, calendarData };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Generate ICS file content
  const generateICSContent = (businessName) => {
    const icsContent = holidays.map(holiday => {
      const startDate = holiday.startDate.replace(/-/g, '');
      const endDate = (holiday.endDate || holiday.startDate).replace(/-/g, '');
      
      return [
        'BEGIN:VEVENT',
        `DTSTART;VALUE=DATE:${startDate}`,
        `DTEND;VALUE=DATE:${endDate}`,
        `SUMMARY:${holiday.name} - CLOSED`,
        `DESCRIPTION:${businessName} is closed - ${holiday.notes || 'Business closure'}`,
        `UID:${holiday.id}@${user.businessId}`,
        holiday.recurring ? 'RRULE:FREQ=YEARLY' : '',
        'END:VEVENT'
      ].filter(Boolean).join('\n');
    }).join('\n');

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Booker.Chat//Business Calendar//EN',
      `X-WR-CALNAME:${businessName} - Business Hours`,
      icsContent,
      'END:VCALENDAR'
    ].join('\n');
  };

  return {
    holidays,
    loading,
    error,
    addHoliday,
    updateHoliday,
    deleteHoliday,
    getHolidaysByType,
    getUpcomingHolidays,
    isHolidayDate,
    generateCalendarIntegration,
    getCalendarIntegration,
    generateICSContent,
    reload: loadHolidays
  };
};